import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../services/authService';
import '../../styles/Auth.css';
import Logo from '../../assets/Logo.png';

const VerifyOTP = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const inputRefs = useRef([]);
    
    const email = searchParams.get('email');
    const type = searchParams.get('type') || 'password-reset'; // 'signup' or 'password-reset'
    const from = searchParams.get('from') || location.state?.from || '/';
    const appliedCoupon = location.state?.appliedCoupon || null;
    const isSignup = type === 'signup';
    const otpLength = isSignup ? 4 : 6;
    
    const [otp, setOtp] = useState(new Array(otpLength).fill(''));
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!email) {
            navigate(isSignup ? '/signup' : '/forgot-password');
        }
    }, [email, navigate, isSignup]);

    useEffect(() => {
        // Focus first input on mount
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleChange = (index, value) => {
        // Only allow digits
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Clear errors when user starts typing
        if (errors.otp) {
            setErrors(prev => ({
                ...prev,
                otp: ''
            }));
        }

        // Move to next input if value is entered
        if (value && index < otpLength - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        // Move to previous input on backspace if current input is empty
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
        
        if (pastedData.length === otpLength) {
            const newOtp = pastedData.split('');
            setOtp(newOtp);
            inputRefs.current[otpLength - 1]?.focus();
        }
    };

    const validateForm = () => {
        const otpString = otp.join('');
        const newErrors = {};

        if (otpString.length !== otpLength) {
            newErrors.otp = `Please enter the complete ${otpLength}-digit OTP`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const otpString = otp.join('');
            
            if (isSignup) {
                // Verify signup OTP
                await authService.verifySignupOTP(email, otpString);
                toast.success('OTP verified successfully!');
                navigate(`/signup?email=${encodeURIComponent(email)}&otp=${otpString}&step=complete&from=${encodeURIComponent(from)}`,
                    { state: { from, appliedCoupon: location.state?.appliedCoupon } }
                );
            } else {
                // Verify password reset OTP
                await authService.verifyOTP(email, otpString);
                toast.success('OTP verified successfully!');
                navigate(`/change-password?email=${encodeURIComponent(email)}&otp=${otpString}`);
            }
        } catch (error) {
            toast.error(error.message || 'Invalid OTP. Please try again.');
            
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        try {
            if (isSignup) {
                await authService.checkEmail(email);
            } else {
                await authService.forgotPassword(email);
            }
            toast.success('OTP sent successfully!');
            setErrors({});
            // Clear OTP inputs
            setOtp(new Array(otpLength).fill(''));
            inputRefs.current[0]?.focus();
        } catch (error) {
            toast.error(error.message || 'Failed to resend OTP. Please try again.');
           
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card">
                <div className="auth-logo">
                    <img src={Logo} alt="Ginni Fashion Studio" />
                </div>
                
                <h1 className="auth-title">Enter OTP</h1>
                <p className="auth-subtitle">
                    We've sent a {otpLength}-digit code to<br />
                    <strong>{email}</strong>
                </p>

                <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                    <div className={`otp-container ${isSignup ? 'signup-otp' : ''}`}>
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={el => inputRefs.current[index] = el}
                                type="text"
                                maxLength="1"
                                value={digit}
                                onChange={(e) => handleChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                className={`otp-input ${errors.otp ? 'error' : ''}`}
                                autoComplete="new-password"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                data-lpignore="true"
                                data-form-type="other"
                            />
                        ))}
                    </div>

                    {errors.otp && (
                        <div className="error-message" style={{ textAlign: 'center', marginBottom: '15px' }}>
                            {errors.otp}
                        </div>
                    )}

                    {errors.submit && (
                        <div className="error-message" style={{ textAlign: 'center', marginBottom: '15px' }}>
                            {errors.submit}
                        </div>
                    )}

                    <button 
                        type="submit" 
                        className="auth-button"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading-spinner"></span>
                                Verifying...
                            </>
                        ) : (
                            'Verify OTP'
                        )}
                    </button>
                </form>

                <div className="auth-link" style={{ color: '#ffffff' }}>
                    Didn't receive the code?{' '}
                    <button 
                        type="button" 
                        onClick={handleResendOTP}
                        style={{ 
                            background: 'none', 
                            border: 'none', 
                            color: '#bc965e', 
                            textDecoration: 'none',
                            cursor: 'pointer',
                            font: 'inherit',
                            fontWeight: '400',
                            fontSize: '14px',
                            transition: 'color 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.color = '#d4a96a';
                            e.target.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.color = '#bc965e';
                            e.target.style.textDecoration = 'none';
                        }}
                    >
                        Resend OTP
                    </button>
                </div>

                <div className="auth-link">
                    <Link to="/login">
                        Back to Login
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default VerifyOTP;